import React, { useState, useEffect, useRef } from 'react';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Loader2, Download, AlertTriangle, FileText } from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import { fmtSoldi, fmtPrezzo, fmtQta, fmtDataIt, ND } from '../../lib/acquisti';

const AcquistiDocumentiPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [fornitori, setFornitori] = useState([]);
  const [categorieCosto, setCategorieCosto] = useState([]);
  const [q, setQ] = useState('');
  const [fornitoreId, setFornitoreId] = useState('tutti');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [documenti, setDocumenti] = useState([]);
  const [caricando, setCaricando] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [scaricando, setScaricando] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    acquistiApi.getFornitori().then(setFornitori).catch(() => {});
    acquistiApi.getStato().then((s) => setCategorieCosto(s.categorie || [])).catch(() => {});
  }, []);

  const salvaCategoriaDocumento = async (documentoId, categoria) => {
    try {
      await acquistiApi.setCategoriaDocumento(documentoId, categoria === NESSUNA ? null : categoria);
      toast.success('Categoria della fattura salvata');
      setDettaglio(await acquistiApi.getDocumento(documentoId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    }
  };

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setCaricando(true);
      try {
        setDocumenti(await acquistiApi.getDocumenti({
          q: q.trim(),
          fornitore_id: fornitoreId === 'tutti' ? '' : fornitoreId,
          data_inizio: dataInizio,
          data_fine: dataFine,
        }));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Errore nel caricamento');
      } finally {
        setCaricando(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, fornitoreId, dataInizio, dataFine]);

  const apri = async (id) => {
    try {
      setDettaglio(await acquistiApi.getDocumento(id));
    } catch (e) {
      toast.error('Documento non trovato');
    }
  };

  const scaricaXml = async (doc) => {
    setScaricando(true);
    try {
      const blob = await acquistiApi.downloadXml(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(doc.numero || 'fattura').replace(/[^\w.-]/g, '_')}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      toast.error('XML originale non disponibile');
    } finally {
      setScaricando(false);
    }
  };

  const d = dettaglio?.documento;

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader titolo="Fatture di acquisto" sottotitolo="Documenti importati e righe originali" />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Numero fattura o fornitore"
              className="h-11 pl-9"
              data-testid="documenti-search-input"
            />
            {caricando && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
          </div>
          <Select value={fornitoreId} onValueChange={setFornitoreId}>
            <SelectTrigger className="h-10" data-testid="documenti-fornitore-select">
              <SelectValue placeholder="Tutti i fornitori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i fornitori</SelectItem>
              {fornitori.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.denominazione}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Dal</Label>
              <Input type="date" value={dataInizio} onChange={(e) => setDataInizio(e.target.value)} className="h-10" data-testid="documenti-data-inizio" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Al</Label>
              <Input type="date" value={dataFine} onChange={(e) => setDataFine(e.target.value)} className="h-10" data-testid="documenti-data-fine" />
            </div>
          </div>
        </div>

        <div className="space-y-2" data-testid="documenti-lista">
          {documenti.map((doc) => (
            <Card
              key={doc.id}
              className="cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => apri(doc.id)}
              data-testid={`documento-item-${doc.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{doc.fornitore_nome}</p>
                    <p className="text-xs text-muted-foreground">
                      n. {doc.numero || ND} · {fmtDataIt(doc.data)} · {doc.n_righe} righe
                    </p>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant={doc.categoria ? 'secondary' : 'outline'} className={`text-[10px] ${doc.categoria ? '' : 'text-amber-700 border-amber-300'}`}>
                        {doc.categoria || 'DA CLASSIFICARE'}
                      </Badge>
                      {doc.segno === -1 && <Badge variant="destructive" className="text-[10px]">{doc.tipo_label}</Badge>}
                      {doc.segno === 1 && doc.tipo_documento !== 'TD01' && (
                        <Badge variant="secondary" className="text-[10px]">{doc.tipo_label}</Badge>
                      )}
                      {doc.anomalie?.length > 0 && (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                          <AlertTriangle className="w-3 h-3 mr-1" />{doc.anomalie.length} segnalazioni
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold tabular-nums">
                      {fmtSoldi((doc.totale || 0) * (doc.segno || 1))}
                    </p>
                    <p className="text-[11px] text-muted-foreground">IVA {fmtSoldi(doc.iva)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {!caricando && documenti.length === 0 && (
            <Card data-testid="documenti-vuoto">
              <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" /> Nessuna fattura trovata con questi filtri.
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Dialog open={!!dettaglio} onOpenChange={(o) => { if (!o) setDettaglio(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base pr-6">
              {d?.fornitore_nome} · n. {d?.numero || ND}
            </DialogTitle>
          </DialogHeader>

          {d && (
            <div className="space-y-3" data-testid="documento-dettaglio">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground text-xs">Data</span><p className="font-semibold">{fmtDataIt(d.data)}</p></div>
                <div><span className="text-muted-foreground text-xs">Tipo</span><p className="font-semibold">{d.tipo_label} ({d.tipo_documento})</p></div>
                <div><span className="text-muted-foreground text-xs">Imponibile</span><p className="font-semibold tabular-nums">{fmtSoldi(d.imponibile)}</p></div>
                <div><span className="text-muted-foreground text-xs">IVA</span><p className="font-semibold tabular-nums">{fmtSoldi(d.iva)}</p></div>
                <div><span className="text-muted-foreground text-xs">Totale documento</span><p className="font-semibold tabular-nums">{fmtSoldi(d.totale)}</p></div>
                <div><span className="text-muted-foreground text-xs">P.IVA/CF</span><p className="font-semibold">{d.fornitore_piva_cf}</p></div>
              </div>

              {d.pagamenti?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Scadenze: {d.pagamenti.map((p, i) => (
                    <span key={i}>{i > 0 ? ' · ' : ''}{fmtDataIt(p.data_scadenza)} {fmtSoldi(p.importo)}</span>
                  ))}
                </div>
              )}

              {d.anomalie?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                  <p className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Segnalazioni</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {d.anomalie.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              <div className="border rounded-lg divide-y">
                {dettaglio.righe.map((r) => (
                  <div key={r.id} className="p-2 text-xs">
                    <p className="font-medium">{r.descrizione || ND}</p>
                    <div className="flex items-center justify-between gap-2 mt-0.5 text-muted-foreground">
                      <span>
                        {fmtQta(r.quantita)} {r.um_originale || ''} × {fmtPrezzo(r.prezzo_unitario)}
                        {r.sconto_percentuale ? ` − ${r.sconto_percentuale}%` : ''}
                        {r.codice_articolo ? ` · cod. ${r.codice_articolo}` : ''}
                      </span>
                      <span className="font-semibold text-foreground tabular-nums shrink-0">{fmtSoldi(r.prezzo_totale)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-2 pt-1 border-t">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Categoria di costo</span>
                {isAdmin ? (
                  <CategoriaSelect
                    valore={d.categoria}
                    categorie={categorieCosto}
                    onChange={(v) => salvaCategoriaDocumento(d.id, v)}
                    testid="documento-categoria-select"
                    className="h-8 w-[180px]"
                  />
                ) : (
                  <Badge variant={d.categoria ? 'secondary' : 'outline'} className="text-[10px]">
                    {d.categoria || 'DA CLASSIFICARE'}
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>Origine: {d.origine} · file {d.nome_file}{d.sdi_id ? ` · SdI ${d.sdi_id}` : ''}</span>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => scaricaXml(d)} disabled={scaricando} data-testid="scarica-xml-btn">
                    {scaricando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                    XML originale
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcquistiDocumentiPage;
